import Vue from 'vue';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import TileWMS from 'ol/source/TileWMS';
import { transform } from 'ol/proj';
import { UrlHandler } from "@/services/utils/UrlHandler.js"
import { mapService } from "@/services/map/mapService";
import { getConnector } from "@/services/externalLayers";
import GeoJSON from 'ol/format/GeoJSON';
import EsriJSON from 'ol/format/EsriJSON';
import * as turf from '@turf/turf';
import { IMPORT_PROVIDER, importedLayerRegistry } from '@/services/layers/importedLayerRegistry';
import { TERRAIN_TECHNICAL_FIELDS, stripPropertiesPublicPrefix, esriGeometryToGeoJSON } from '@/services/layers/attributesTable.js';
import { configuredTitle } from '@/services/layers/fieldsConfig.js';
import { formatDateValue, isZonedIsoDate } from '@/services/helpers/displayValueHelper';

// --- HITBOX TOLERANCE CONSTANTS ---
// Tune these to match the largest point symbol size in your GeoServer SLDs / ArcGIS styles.
const WMS_HIT_BUFFER_PX = 16;       // pixels expanded around click for WMS GetFeatureInfo (GeoServer BUFFER param)
const ARCGIS_HIT_TOLERANCE_PX = 16; // screen-pixel radius used to build the ArcGIS spatial envelope
const FEATURE_INFO_TIMEOUT_MS = 10000; // GetFeatureInfo no tiene reintento — si el servidor no responde en este tiempo, se reporta como error

// Preferencia de "popup maximizado" (side panel) vs. popup chico — se guarda
// en localStorage (no sessionStorage) para que sobreviva entre visitas, no
// solo dentro de la misma pestaña/sesión del navegador.
const MAXIMIZED_PREFERENCE_KEY = 'featurePopup.maximizedPreference';

function readMaximizedPreference() {
    try {
        return localStorage.getItem(MAXIMIZED_PREFERENCE_KEY) === 'true';
    } catch (error) {
        // localStorage no disponible (modo privado, cuota, SSR) — se usa el
        // valor por defecto sin persistir.
        return false;
    }
}

function writeMaximizedPreference(value) {
    try {
        localStorage.setItem(MAXIMIZED_PREFERENCE_KEY, value ? 'true' : 'false');
    } catch (error) {
        // Ídem — si no se puede guardar, la preferencia simplemente no persiste.
    }
}

class SelectService {
    constructor() {
        this.map = null;
        this.drawInteraction = null;
        this.selectionLayer = null;
        this.activeLayers = [];
        this.clickListener = null;

        // Reactive State for UI — currentIndex y maximized viven acá (no como
        // data local de FeaturePopup.vue) porque FeaturePopup.vue y
        // FeaturePopupExpanded.vue son dos componentes distintos que deben
        // mostrar siempre la MISMA feature seleccionada y el mismo estado de
        // maximizado, nunca cada uno el suyo por separado. maximized arranca
        // en la preferencia guardada (ver readMaximizedPreference) para que
        // el usuario no tenga que volver a maximizar en cada visita.
        this.state = Vue.observable({
            features: [],
            location: null,
            errors: [],
            currentIndex: 0,
            maximized: readMaximizedPreference(),
        });
    }

    init(map) {
        this.map = map;
        this.createSelectionLayer();
    }

    setMap(map) {
        this.map = map;
    }

    setActiveLayers(layers) {
        this.activeLayers = layers || [];
    }

    createSelectionLayer() {
        if (!this.selectionLayer) {
            this.selectionLayer = new VectorLayer({
                source: new VectorSource(),
                zIndex: 9999
            });
        }
    }

    isListening() {
        return this.clickListener !== null;
    }

    // --- STATE MANAGEMENT ---

    setSelection(coordinate, features) {
        this.state.location = coordinate;
        this.state.features = features || [];
        this.state.currentIndex = 0;
        // Una selección nueva retoma la preferencia guardada (no siempre
        // arranca minimizada) — clearResults() más abajo apaga el flag
        // visual al cerrar, pero nunca borra la preferencia persistida.
        this.state.maximized = readMaximizedPreference();
    }

    clearResults() {
        this.state.location = null;
        this.state.features = [];
        this.state.errors = [];
        this.state.currentIndex = 0;
        this.state.maximized = false;

        if (this.selectionLayer) {
            this.selectionLayer.getSource().clear();
        }
    }

    setCurrentIndex(index) {
        this.state.currentIndex = index;
    }

    setMaximized(value) {
        this.state.maximized = value;
        writeMaximizedPreference(value);
    }

    // --- DATA FETCHING (Clean Code Segregated Methods) ---

    async getPointFeatures(coordinate, resolution, projection, layers = null, pixel = null) {
        console.log('Fetching point features');
        const targetLayers = layers || this.activeLayers;
        if (!targetLayers || targetLayers.length === 0) {
            this.state.errors = [];
            return [];
        }
        console.log('Target layers:', targetLayers);
        const remoteLayers = targetLayers.filter(layer => layer.provider?.name !== IMPORT_PROVIDER);
        const promises = [...remoteLayers].reverse().map(async layer => {
            const provider = layer.provider ? layer.provider.name : 'wms';
            try {
                let result = null;
                if (provider === 'geoserver') {
                    result = await this.fetchWMSFeatureInfo(layer, coordinate, resolution, projection);
                } else if (provider === 'arcgis') {
                    result = await this.fetchArcGISFeatureInfo(layer, coordinate, resolution);
                } else {
                    const connector = getConnector(provider);
                    if (connector) {
                        const olLayer = mapService.getOlLayerById(layer.id);
                        result = await connector.queryFeatures({ layer, coordinate, resolution, projection, pixel, map: this.map, olLayer });
                    }
                }
                // terrainFields (fieldId + label real de la pregunta) es la marca de
                // que layer es una Capa_Terreno — ver terrainGeoserverDescriptor en
                // TerrainSection.vue. Se propaga hasta el feature para que
                // getFeatureProperties pueda mostrar solo propertiesPublic.* con su
                // label, igual que ya hace la tabla de atributos.
                if (result && layer.provider?.terrainFields) {
                    result.terrainFields = layer.provider.terrainFields;
                }
                // Misma idea que terrainFields: la configuración de Campos vive en la capa
                // y el popup solo ve la feature, así que se adjunta acá para que
                // getFeatureProperties pueda filtrar/ordenar/renombrar.
                if (result && layer.campos) {
                    result.campos = layer.campos;
                }
                return result;
            } catch (error) {
                // Fallo de red (ej. CORS bloqueado por un servidor privado) — no confundir
                // con "sin features en este punto", se reporta aparte en state.errors.
                console.warn(`Error fetching info for layer ${layer.name}:`, error);
                return { error: true, layerName: layer.name };
            }
        });

        const results = await Promise.all(promises);

        this.state.errors = results
            .filter(res => res && res.error)
            .map(res => res.layerName);

        // Flatten and clean results (remove nulls y entradas de error)
        const remoteFeatures = results
            .filter(res => res && !res.error && res.features && res.features.length > 0)
            .flatMap(res => res.features.map(f => ({
                ...f,
                layerName: res.layerName, // Ensure layer name is attached
                terrainFields: res.terrainFields,
                campos: res.campos,
                fieldTypes: res.fieldTypes,
            })));
        return [...this.getImportedPointFeatures(pixel, targetLayers), ...remoteFeatures];
    }

    getImportedPointFeatures(pixel, layers) {
        if (!pixel || !this.map) return [];
        const descriptors = new Map((layers || [])
            .filter(layer => layer.provider?.name === IMPORT_PROVIDER)
            .map(layer => [layer.id, layer]));
        if (!descriptors.size) return [];
        const features = [];
        const seen = new Set();
        this.map.forEachFeatureAtPixel(pixel, (feature, olLayer) => {
            const descriptor = olLayer && descriptors.get(olLayer.get('id'));
            if (!descriptor || seen.has(feature)) return;
            seen.add(feature);
            const properties = { ...feature.getProperties() };
            delete properties.geometry;
            features.push({ type: 'Feature', properties, layerName: descriptor.name });
        }, {
            hitTolerance: WMS_HIT_BUFFER_PX,
            layerFilter: olLayer => descriptors.has(olLayer.get('id')),
        });
        return features;
    }

    /**
     * fetch() con timeout — GetFeatureInfo no reintenta, así que si el servidor no responde
     * en FEATURE_INFO_TIMEOUT_MS se aborta en vez de dejar la consulta colgada sin feedback.
     * El AbortError se deja propagar: el try/catch por capa en getPointFeatures ya lo
     * convierte en { error: true, layerName }.
     */
    async fetchWithTimeout(url, layerName) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), FEATURE_INFO_TIMEOUT_MS);
        try {
            return await fetch(url, { signal: controller.signal });
        } catch (error) {
            if (error.name === 'AbortError') {
                console.warn(`GetFeatureInfo para "${layerName}" superó el tiempo de espera (${FEATURE_INFO_TIMEOUT_MS / 1000}s).`);
            }
            throw error;
        } finally {
            clearTimeout(timeoutId);
        }
    }

    /**
     * Fetches GetFeatureInfo from a WMS source (GeoServer)
     */
    async fetchWMSFeatureInfo(layer, coordinate, resolution, projection) {
        console.log('Fetching WMS feature info');
        let url = UrlHandler.getBaseUrl(layer) + '/wms';

        const layersParam = layer.provider.geoserverdata ?
            `${layer.provider.geoserverdata.workspace}:${layer.provider.geoserverdata.datastore}` :
            layer.name;

        // Read the active CQL_FILTER from the OL layer source
        const activeFilter = mapService.getLayerActiveFilter(layer.id);

        const wmsParams = {
            'LAYERS': layersParam,
            'TILED': true,
            'VERSION': '1.1.1',
        };
        if (activeFilter && activeFilter.cqlFilter) {
            wmsParams['CQL_FILTER'] = activeFilter.cqlFilter;
        }

        const wmsSource = new TileWMS({ url, params: wmsParams });

        const featureInfoUrl = wmsSource.getFeatureInfoUrl(
            coordinate,
            resolution,
            projection,
            {
                'INFO_FORMAT': 'application/json',
                'BUFFER': WMS_HIT_BUFFER_PX
            }
        );
        console.log('Feature info URL:', featureInfoUrl);

        if (!featureInfoUrl) return null;

        const response = await this.fetchWithTimeout(featureInfoUrl, layer.name);
        if (!response.ok) {
            console.warn(`GetFeatureInfo falló para "${layer.name}": HTTP ${response.status}`);
            return { error: true, layerName: layer.name };
        }

        // Algunos servidores devuelven 200 OK con un ServiceExceptionReport en XML cuando
        // no soportan INFO_FORMAT=application/json — evita que response.json() explote con
        // un SyntaxError críptico y lo reporta como el error real que es.
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('json')) {
            console.warn(`GetFeatureInfo para "${layer.name}" no devolvió JSON (Content-Type: ${contentType || 'desconocido'}) — el servidor probablemente no soporta INFO_FORMAT=application/json.`);
            return { error: true, layerName: layer.name };
        }

        const data = await response.json();
        return {
            layerName: layer.name,
            features: data.features || []
        };
    }

    /**
     * Fetches query data from ArcGIS REST Service
     */
    async fetchArcGISFeatureInfo(layer, coordinate, resolution) {
        const baseUrl = UrlHandler.getArcgisUrl(layer)
        const buffer = ARCGIS_HIT_TOLERANCE_PX * (resolution || 1);
        const envelope = {
            xmin: coordinate[0] - buffer,
            ymin: coordinate[1] - buffer,
            xmax: coordinate[0] + buffer,
            ymax: coordinate[1] + buffer,
            spatialReference: { wkid: 3857 }
        };

        // Read active LAYERDEFS to extract the WHERE clause for this sublayer
        const activeFilter = mapService.getLayerActiveFilter(layer.id);
        let whereClause = '1=1';
        if (activeFilter && activeFilter.featureWhere) whereClause = activeFilter.featureWhere;
        if (activeFilter && activeFilter.layerDefs) {
            try {
                const defs = JSON.parse(activeFilter.layerDefs);
                whereClause = defs[String(layer.codigo)] || '1=1';
            } catch (e) {
                // malformed LAYERDEFS — fall back to no filter
            }
        }

        const params = new URLSearchParams({
            f: 'json',
            returnGeometry: 'true',
            spatialRel: 'esriSpatialRelIntersects',
            geometry: JSON.stringify(envelope),
            geometryType: 'esriGeometryEnvelope',
            outFields: '*',
            outSR: '3857',
            where: whereClause,
        });
        const response = await fetch(`${baseUrl}/query`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: params.toString(),
        });
        if (!response.ok) return null;
        const data = await response.json();
        const features = (data.features || []).map(f => ({
            type: 'Feature',
            geometry: null,
            properties: f.attributes,
            id: f.attributes.OBJECTID
        }));
        return {
            layerName: layer.name,
            features: features,
            fieldTypes: Object.fromEntries((data.fields || []).map(field => [field.name, field.type]))
        };
    }

    /**
     * Igual que fetchArcGISFeatureInfo, pero conservando la geometría — usado
     * por targetFeatureSelectionService.js (selección por clic de entidades
     * objetivo del filtro entre-capas) para poder resaltarlas en el mapa. No
     * se modifica fetchArcGISFeatureInfo porque el popup de "Consulta" no
     * necesita la geometría (sólo atributos).
     */
    async fetchArcGISFeatureInfoWithGeometry(layer, coordinate, resolution) {
        const baseUrl = UrlHandler.getArcgisUrl(layer)
        const buffer = ARCGIS_HIT_TOLERANCE_PX * (resolution || 1);
        const envelope = {
            xmin: coordinate[0] - buffer,
            ymin: coordinate[1] - buffer,
            xmax: coordinate[0] + buffer,
            ymax: coordinate[1] + buffer,
            spatialReference: { wkid: 3857 }
        };
        const activeFilter = mapService.getLayerActiveFilter(layer.id);
        let whereClause = '1=1';
        if (activeFilter && activeFilter.featureWhere) whereClause = activeFilter.featureWhere;
        if (activeFilter && activeFilter.layerDefs) {
            try {
                const defs = JSON.parse(activeFilter.layerDefs);
                whereClause = defs[String(layer.codigo)] || '1=1';
            } catch (e) {
                // malformed LAYERDEFS — fall back to no filter
            }
        }
        const params = new URLSearchParams({
            f: 'json',
            returnGeometry: 'true',
            spatialRel: 'esriSpatialRelIntersects',
            geometry: JSON.stringify(envelope),
            geometryType: 'esriGeometryEnvelope',
            outFields: '*',
            outSR: '3857',
            where: whereClause,
        });
        const response = await fetch(`${baseUrl}/query`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: params.toString(),
        });
        if (!response.ok) return null;
        const data = await response.json();
        const features = (data.features || []).map(f => ({
            type: 'Feature',
            geometry: esriGeometryToGeoJSON(f.geometry),
            properties: f.attributes,
            id: f.attributes.OBJECTID
        }));
        return {
            layerName: layer.name,
            features: features
        };
    }


    async getAreaFeatures(geometry, layers = null) {
        const targetLayers = layers || this.activeLayers;
        if (!targetLayers || targetLayers.length === 0) {
            return [];
        }

        const remoteLayers = targetLayers.filter(layer => layer.provider?.name !== IMPORT_PROVIDER);
        const promises = [...remoteLayers].reverse().map(async layer => {
            const provider = layer.provider ? layer.provider.name : 'wms';

            try {
                let result = null;
                if (provider === 'geoserver') {
                    result = await this.fetchWFSFeatureInfo(layer, geometry);
                } else if (provider === 'arcgis') {
                    result = await this.fetchArcGISQuery(layer, geometry);
                }
                if (result && layer.campos) {
                    result.campos = layer.campos;
                }
                return result;
            } catch (error) {
                console.warn(`Error fetching area info for layer ${layer.name}:`, error);
                return null;
            }
        });

        const results = await Promise.all(promises);

        const remoteFeatures = results
            .filter(res => res && res.features && res.features.length > 0)
            .flatMap(res => res.features.map(f => ({
                ...f,
                layerName: res.layerName,
                campos: res.campos
            })));
        return [...this.getImportedAreaFeatures(geometry, targetLayers), ...remoteFeatures];
    }

    getImportedAreaFeatures(geometry, layers) {
        let selection;
        try {
            selection = turf.toWgs84(turf.feature(new GeoJSON().writeGeometryObject(geometry)));
        } catch (error) {
            return [];
        }
        return (layers || [])
            .filter(layer => layer.provider?.name === IMPORT_PROVIDER)
            .flatMap(layer => {
                const entry = importedLayerRegistry.get(layer.id);
                if (!entry) return [];
                return entry.visibleRecords.filter(record => {
                    try {
                        return record.row.geometry && turf.booleanIntersects(
                            turf.toWgs84(turf.feature(record.row.geometry)),
                            selection
                        );
                    } catch (error) { return false; }
                }).map(record => {
                    const properties = { ...record.row };
                    delete properties.geometry;
                    delete properties.bbox;
                    const sourceIndex = entry.records.indexOf(record);
                    const featureId = record.feature?.getId?.();
                    return {
                        type: 'Feature',
                        id: featureId != null ? `${layer.id}:${featureId}` : `${layer.id}:${sourceIndex}`,
                        geometry: record.row.geometry,
                        properties,
                        layerName: layer.name,
                    };
                });
            });
    }

    /**
     * WFS Complex Logic for Geoserver Area Selection
     */
    async fetchWFSFeatureInfo(layer, polygonGeometry) {
        const geoserverUrl = UrlHandler.getBaseUrl(layer)
        const workspace = layer.provider.geoserverdata.workspace;
        const datastore = layer.provider.geoserverdata.datastore;
        const layerName = `${workspace}:${datastore}`;

        // 1. Get Geometry Column Name (e.g. 'the_geom', 'shape')
        const geometryColumn = await this.getGeometryColumnName(workspace, datastore, geoserverUrl);
        if (!geometryColumn) return null;

        // 2. Get Layer SRID (e.g. '32719')
        const srid = await this.getSridFromGeoServer(workspace, datastore, geoserverUrl);
        if (!srid) return null;

        // 3. Transform Polygon to Layer SRID
        const coords = polygonGeometry.getCoordinates()[0];
        const transformedCoords = coords.map(coord =>
            transform(coord, "EPSG:3857", "EPSG:" + srid)
        );

        const polygonString = transformedCoords.map(c => c.join(" ")).join(",");

        // 4. Construct CQL Filter
        const spatialCql = `INTERSECTS(${geometryColumn}, SRID=${srid};POLYGON((${polygonString})))`;
        const activeFilter = mapService.getLayerActiveFilter(layer.id);
        const cqlFilter = activeFilter && activeFilter.cqlFilter
            ? `(${activeFilter.cqlFilter}) AND (${spatialCql})`
            : spatialCql;

        // 5. Fetch WFS
        const params = new URLSearchParams();
        params.append("service", "WFS");
        params.append("version", "2.0.0");
        params.append("request", "GetFeature");
        params.append("typeNames", layerName);
        params.append("outputFormat", "application/json");
        // El resto de las entradas del geoproceso (ArcGIS, dibujo e
        // importaciones) trabaja en Web Mercator. Pedir el WFS en el mismo
        // CRS deja un único contrato geométrico, independiente del origen.
        params.append("srsName", "EPSG:3857");
        params.append("CQL_FILTER", cqlFilter);

        const response = await fetch(`${geoserverUrl}/wfs`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: params.toString()
        });

        if (!response.ok) return null;
        const data = await response.json();

        return {
            layerName: layer.name,
            features: data.features || []
        };
    }

    async fetchArcGISQuery(layer, geometry) {
        const baseUrl = UrlHandler.getArcgisUrl(layer)
        const activeFilter = mapService.getLayerActiveFilter(layer.id);
        let whereClause = activeFilter?.featureWhere || '1=1';
        if (activeFilter && activeFilter.layerDefs) {
            try {
                const defs = JSON.parse(activeFilter.layerDefs);
                whereClause = defs[String(layer.codigo)] || whereClause;
            } catch (error) { /* keep default */ }
        }

        const rings = geometry.getCoordinates();
        const esriGeometry = {
            rings: rings,
            spatialReference: { wkid: 3857 }
        };

        const params = new URLSearchParams({
            f: 'json',
            returnGeometry: 'true',
            spatialRel: 'esriSpatialRelIntersects',
            geometry: JSON.stringify(esriGeometry),
            geometryType: 'esriGeometryPolygon',
            outFields: '*',
            outSR: '3857',
            where: whereClause,
        });

        const queryUrl = `${baseUrl}/query`;
        const response = await fetch(queryUrl, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: params.toString()
        });

        if (!response.ok) return null;
        const data = await response.json();

        const format = new EsriJSON();
        const geoJSONFormat = new GeoJSON();
        // Parse EsriJSON to OL Features then write to GeoJSON POJOs
        const olFeatures = format.readFeatures(data);
        const geoFeatures = olFeatures.map(f => geoJSONFormat.writeFeatureObject(f));

        return {
            layerName: layer.name,
            features: geoFeatures
        };
    }


    async getGeometryColumnName(workspace, datastore, geoserverUrl) {
        const layerName = `${workspace}:${datastore}`;
        try {
            const wfsUrl = `${geoserverUrl}/wfs?service=wfs&version=2.0.0&request=DescribeFeatureType&typeNames=${layerName}`;
            const response = await fetch(wfsUrl);
            const text = await response.text();

            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(text, "text/xml");

            const elements = xmlDoc.getElementsByTagName("xsd:element");
            for (const element of elements) {
                const type = element.getAttribute("type");
                if (type && type.includes("gml:")) {
                    return element.getAttribute("name");
                }
            }
            return 'the_geom'; // fallback
        } catch (e) {
            console.warn("Failed to get geometry column", e);
            return 'the_geom';
        }
    }

    async getSridFromGeoServer(workspace, datastore, geoserverUrl) {
        try {
            const layerName = `${workspace}:${datastore}`;
            const url = `${geoserverUrl}/wfs?service=WFS&version=2.0.0&request=GetFeature&typeNames=${layerName}&count=1&outputFormat=application/json`;
            const response = await fetch(url);
            const data = await response.json();

            if (data.crs && data.crs.properties && data.crs.properties.name) {
                return data.crs.properties.name.split(":").pop();
            }
            return '3857'; // default
        } catch (e) {
            return '3857';
        }
    }


    // --- POINT SELECTION ---
    enablePointSelection() {
        this.disable();
        if (!this.map) return;

        this.map.getViewport().style.cursor = 'pointer';
        this.clickListener = async (evt) => {
            if (this.map.get('suppressFeaturePopups')) return;
            const view = this.map.getView();
            const resolution = view.getResolution();
            const projection = view.getProjection();

            const features = await this.getPointFeatures(evt.coordinate, resolution, projection, null, evt.pixel);

            // Setup selection
            this.setSelection(evt.coordinate, features);
        };

        this.map.on('singleclick', this.clickListener);
    }

    // QA-204: enableAreaSelection (modos Polígono/Círculo del panel Consulta)
    // se eliminó junto con la herramienta; queda solo la consulta por punto.

    disable() {
        if (!this.map) return;

        this.map.getViewport().style.cursor = '';

        if (this.clickListener) {
            this.map.un('singleclick', this.clickListener);
            this.clickListener = null;
        }

        if (this.drawInteraction) {
            this.map.removeInteraction(this.drawInteraction);
            this.drawInteraction = null;
        }

        this.clearResults();

        if (this.selectionLayer) {
            this.map.removeLayer(this.selectionLayer);
        }
    }
}

export const selectService = new SelectService();

// getFeatureLayerName/getFeatureProperties viven acá (no como método de
// FeaturePopup.vue) porque FeaturePopup.vue y FeaturePopupExpanded.vue
// necesitan formatear exactamente igual la misma feature seleccionada.
export function getFeatureLayerName(feature) {
    if (!feature) return 'Capa Desconocida';
    // Handle OpenLayers Feature
    if (typeof feature.get === 'function') {
        return feature.get('layerName') || 'Capa Desconocida';
    }
    // Handle POJO
    return feature.layerName || 'Capa Desconocida';
}

// QA-206: título del popup según el campo marcado `titulo` en la
// configuración de Campos de la capa. Devuelve null cuando no hay campo
// titulo con valor — el componente cae entonces a getFeatureLayerName.
export function getFeatureTitle(feature) {
    if (!feature) return null;
    const props = typeof feature.getProperties === 'function'
        ? feature.getProperties()
        : (feature.properties || feature.attributes || {});
    const campos = typeof feature.get === 'function' ? feature.get('campos') : feature.campos;
    return configuredTitle(props, campos);
}

const TERRAIN_ATTACHMENT_VALUE = '__terrainAttachmentValue';
const TERRAIN_ATTACHMENT_IDS_SUFFIX = '.attachmentIds';
const TERRAIN_ATTACHMENT_NAMES_SEGMENT = '.attachmentNames.';
const TERRAIN_IMAGE_EXTENSION = /\.(jpe?g|png|webp)$/i;

export function parseTerrainAttachmentIds(value) {
    if (Array.isArray(value)) return value.map(String).filter(Boolean);
    if (typeof value !== 'string') return [];
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
    } catch (error) { /* GeoServer también entrega arreglos BSON sin comillas. */ }
    return trimmed
        .replace(/^\[|\]$/g, '')
        .split(',')
        .map(item => item.trim().replace(/^['"]|['"]$/g, ''))
        .filter(Boolean);
}

function terrainAttachmentKind(attachmentIds, attachmentNames) {
    if (!attachmentIds.length) return 'file';
    const names = attachmentIds.map(id => attachmentNames[id]).filter(Boolean);
    return names.length === attachmentIds.length && names.every(name => TERRAIN_IMAGE_EXTENSION.test(name)) ? 'photo' : 'file';
}

export function isTerrainAttachmentValue(value) {
    return Boolean(value && value[TERRAIN_ATTACHMENT_VALUE]);
}

export function createTerrainAttachmentValue(attachmentIds, attachmentNames, label) {
    const ids = Array.isArray(attachmentIds) ? attachmentIds.map(String).filter(Boolean) : [];
    const names = attachmentNames || {};
    return {
        [TERRAIN_ATTACHMENT_VALUE]: true,
        attachmentIds: ids,
        attachmentNames: names,
        kind: terrainAttachmentKind(ids, names),
        label,
    };
}

function terrainDisplayProperties(properties, terrainFields) {
    const labelsByFieldId = new Map(terrainFields.map(field => [field.fieldId, field.label]));
    const display = {};
    const attachmentsByField = new Map();

    const ensureAttachment = fieldId => {
        if (!attachmentsByField.has(fieldId)) {
            const attachment = {
                [TERRAIN_ATTACHMENT_VALUE]: true,
                attachmentIds: [],
                attachmentNames: {},
                kind: 'file',
                label: labelsByFieldId.get(fieldId) || fieldId,
            };
            attachmentsByField.set(fieldId, attachment);
            display[attachment.label] = attachment;
        }
        return attachmentsByField.get(fieldId);
    };

    for (const [key, value] of Object.entries(properties)) {
        if (key === 'bbox' || key === 'geometry' || TERRAIN_TECHNICAL_FIELDS.includes(key)) continue;
        const fieldId = stripPropertiesPublicPrefix(key);
        if (TERRAIN_TECHNICAL_FIELDS.includes(fieldId)) continue;

        if (fieldId.endsWith(TERRAIN_ATTACHMENT_IDS_SUFFIX)) {
            const baseFieldId = fieldId.slice(0, -TERRAIN_ATTACHMENT_IDS_SUFFIX.length);
            ensureAttachment(baseFieldId).attachmentIds = parseTerrainAttachmentIds(value);
            continue;
        }

        const namesIndex = fieldId.indexOf(TERRAIN_ATTACHMENT_NAMES_SEGMENT);
        if (namesIndex >= 0) {
            const baseFieldId = fieldId.slice(0, namesIndex);
            const attachmentId = fieldId.slice(namesIndex + TERRAIN_ATTACHMENT_NAMES_SEGMENT.length);
            if (attachmentId) ensureAttachment(baseFieldId).attachmentNames[attachmentId] = String(value || '');
            continue;
        }

        if (value && typeof value === 'object' && Array.isArray(value.attachmentIds)) {
            const attachment = ensureAttachment(fieldId);
            attachment.attachmentIds = value.attachmentIds.map(String);
            attachment.attachmentNames = value.attachmentNames || {};
            continue;
        }

        if (typeof value !== 'object' && typeof value !== 'function') {
            display[labelsByFieldId.get(fieldId) || fieldId] = value;
        }
    }

    for (const attachment of attachmentsByField.values()) {
        attachment.kind = terrainAttachmentKind(attachment.attachmentIds, attachment.attachmentNames);
        if (!attachment.attachmentIds.length) delete display[attachment.label];
    }
    return display;
}

/**
 * Aplica la configuración de Campos de la capa (paso 2 del formulario ArcGIS):
 * oculta los no visibles, ordena por `order` y usa `alias` como etiqueta.
 *
 * Si ninguna fila configurada calza con las propiedades de la feature, o si
 * ninguna viene con `visible` truthy, se devuelven las propiedades tal cual:
 * `visible` se guarda con omitempty (models/layer.go:48), así que las
 * configuraciones antiguas llegan sin la clave y filtrar a rajatabla dejaría
 * el popup vacío. Mismo fallback que ya usa pdf_service.py:163 con `reporte`.
 */
function applyLayerFields(properties, campos) {
    if (!Array.isArray(campos) || !campos.length) return properties;

    const visibles = campos.filter(campo => campo.visible && campo.name in properties);
    if (!visibles.length) return properties;

    visibles.sort((a, b) => (a.order || 0) - (b.order || 0));

    const display = {};
    for (const campo of visibles) {
        // Alias vacío o repetido cae al nombre crudo: dos alias iguales colapsarían
        // en una sola clave y se perdería una columna.
        const label = campo.alias && !(campo.alias in display) ? campo.alias : campo.name;
        display[label] = properties[campo.name];
    }
    return display;
}

export function getFeatureProperties(feature) {
    if (!feature) return {};
    let props = {};

    // Handle OpenLayers Feature
    if (typeof feature.getProperties === 'function') {
        props = feature.getProperties();
    } else {
        // Handle POJO
        props = feature.properties || feature.attributes || {};
    }

    // GeoServer aplana las respuestas complejas de Terreno, por ejemplo
    // propertiesPublic.<fieldId>.attachmentNames.<attachmentId>. Reconstruir
    // esos grupos antes de descartar objetos permite que ambos popups usen la
    // misma galería de adjuntos que la tabla de revisión.
    const terrainFields = typeof feature.get === 'function' ? feature.get('terrainFields') : feature.terrainFields;
    if (Array.isArray(terrainFields) && terrainFields.length) {
        return terrainDisplayProperties(props, terrainFields);
    }

    // fieldTypes: tipos de fields[] de la respuesta ArcGIS (fetchArcGISFeatureInfo);
    // GeoServer no trae tipos, ahí solo se reconocen strings ISO con zona.
    const fieldTypes = (typeof feature.get === 'function' ? feature.get('fieldTypes') : feature.fieldTypes) || {};
    const filtered = {};
    // Filter out internal or complex objects
    for (const [key, value] of Object.entries(props)) {
        // Filter out 'geometry' explicitly if present in properties
        if (key !== 'bbox' && key !== 'geometry' && typeof value !== 'object' && typeof value !== 'function') {
            const isDate = (fieldTypes[key] === 'esriFieldTypeDate' && typeof value === 'number') || isZonedIsoDate(value);
            filtered[key] = isDate ? formatDateValue(value) : value;
        }
    }

    // Las capas de catálogo mantienen su configuración tradicional de campos.
    const campos = typeof feature.get === 'function' ? feature.get('campos') : feature.campos;
    return applyLayerFields(filtered, campos);
}
